/**
 * LOTE B — Igualas (facturas recurrentes) de una oportunidad, en su pestaña de
 * facturación, junto a los hitos puntuales.
 *
 * 05-10-2026, Jorge: «cuando entro en un proyecto quiero saber TODOS los hitos
 * de pago, los puntuales y los recurrentes». Antes este panel hacía
 * `return null` cuando no había ninguna iguala: la ficha no decía nada, ni
 * siquiera que el concepto existiera, y había que irse a Facturación → Pagos
 * recurrentes. Ahora:
 *
 *  - si no hay igualas, el panel SE PINTA IGUAL y lo dice (un bloque que se
 *    esconde cuando está vacío miente por omisión);
 *  - en un PROYECTO se puede crear la iguala aquí mismo, con cliente y
 *    proyecto fijados, que es lo que garantiza que vuelva a verse en la ficha;
 *  - y se avisa de las líneas recurrentes PACTADAS EN LA VENTA que todavía no
 *    tienen iguala. Eso es lo que pasó con Tour Time y con FIAS: la
 *    recurrencia quedó registrada en la oportunidad, pero eso es previsión
 *    comercial; hasta que no existe la iguala no se factura nada.
 *
 * ☠️ Tres cosas que la revisión del 05-10 corrigió y conviene no deshacer:
 *
 *  1. NADA de esto se ofrece en una oportunidad que no está ganada. Lo pactado
 *     en una propuesta no está vendido; invitar a crear la iguala allí nace un
 *     cobro ACTIVE que el proceso nocturno factura a un cliente que no ha
 *     firmado, y la guardia de regresión no lo frena (solo mira transiciones
 *     DESDE una etapa ganada). En una oportunidad abierta esto es informativo.
 *  2. Un fallo al leer NO es «no hay igualas». Decir «este proyecto no tiene
 *     ningún pago recurrente» porque se cayó la red lleva a crear una segunda
 *     iguala y a facturar dos veces.
 *  3. Lo pactado se materializa con el PLAN DE FACTURACIÓN, no con el botón de
 *     alta: el plan respeta la vigencia (nº de cuotas) y el ancla de inicio
 *     («al entregar»), y este formulario no tiene esos campos. Por eso el
 *     bloque ámbar explica y no manda crear.
 */

import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Repeat, ExternalLink, Plus, AlertTriangle, AlertCircle } from 'lucide-react'
import { api } from '../../lib/api'
import { formatCents } from '../../lib/money'
import { currentLocaleTag } from '../../lib/formatting'
import { useAuth } from '../../contexts/AuthContext'
import RecurringFormModal from './RecurringFormModal'
import type {
  OpportunityServiceLineDto,
  RecurringInvoiceDto,
  RecurringInvoiceListResponse,
} from '@jr2os/shared'

interface Props {
  opportunityId: string
  /** Cliente: hace falta para crear la iguala sin elegirlo a mano. */
  clientId?: string
  canWrite?: boolean
  /** Líneas de la oportunidad, para enseñar lo pactado que aún no factura. */
  serviceLines?: OpportunityServiceLineDto[]
  /** Etapa ganada: solo entonces esto es un compromiso y no una previsión. */
  isWon?: boolean
  /** Modo de facturación de la oportunidad: sin FACTURABLE no hay iguala posible. */
  billingMode?: string
}

/** Importe por periodo (suma de líneas: cantidad × precio unitario). */
function perPeriodCents(r: RecurringInvoiceDto): number {
  return r.lines.reduce((s, l) => s + Math.round((Number(l.quantity) || 0) * l.unitPriceCents), 0)
}

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/30',
  PAUSED: 'text-amber-700 dark:text-amber-300 bg-amber-500/10 border-amber-500/30',
  CANCELED: 'text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30',
  COMPLETED: 't-muted bg-[var(--bg-hover)] border-[var(--border)]',
}

export default function OpportunityRecurringPanel({
  opportunityId,
  clientId,
  canWrite = false,
  serviceLines = [],
  isWon = false,
  billingMode,
}: Props) {
  const { t } = useTranslation(['billing', 'enums'])
  const auth = useAuth()
  const [items, setItems] = useState<RecurringInvoiceDto[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    setFailed(false)
    try {
      const resp = await api.get<RecurringInvoiceListResponse>(
        `/recurring-invoices?opportunityId=${opportunityId}`
      )
      setItems(resp.items)
    } catch {
      // Ni se afirma que no hay nada ni se ofrece crear: no se ha podido mirar.
      setItems(null)
      setFailed(true)
    }
  }, [opportunityId])

  useEffect(() => {
    void load()
  }, [load])

  // Crear una iguala es cosa de ADMIN o CONSULTOR (el endpoint lo exige), de
  // una oportunidad ya ganada y facturable. Un ANALISTA asignado tiene permiso
  // de escritura en la ficha pero no en facturación: ofrecérselo sería llevarlo
  // a rellenar el formulario entero para comerse un 403 al final.
  const rol = auth.status === 'authenticated' ? auth.user.role : null
  const puedeCrear =
    canWrite &&
    !!clientId &&
    isWon &&
    !failed &&
    billingMode === 'FACTURABLE' &&
    (rol === 'ADMIN' || rol === 'CONSULTOR')

  // Lo pactado en la venta que todavía no factura. Mismo filtro que usa el
  // asistente del plan de facturación al materializarlo: solo lo FACTURABLE y
  // con importe. Sin esto se listaba trabajo probono —que por definición no se
  // factura— invitando a cobrarlo.
  const pactadasSinIguala =
    isWon && items !== null && items.length === 0
      ? serviceLines.filter(
          (l) => l.isRecurring && l.billingMode === 'FACTURABLE' && l.valueCents > 0
        )
      : []

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(currentLocaleTag(), {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })

  return (
    <div className="surface p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2 text-sm">
          <Repeat size={15} className="text-teal-600 dark:text-teal-300" />
          {t('recurringPanel.title')}
        </h3>
        <div className="flex items-center gap-3">
          {puedeCrear && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="text-xs text-jr2-700 dark:text-jr2-300 hover:underline flex items-center gap-1"
            >
              <Plus size={12} /> {t('recurringPanel.create')}
            </button>
          )}
          <Link
            to="/settings/recurring"
            className="text-xs t-muted hover:text-jr2-700 dark:hover:text-jr2-200 flex items-center gap-1"
          >
            {t('recurringPanel.manage')} <ExternalLink size={12} />
          </Link>
        </div>
      </div>

      {failed ? (
        <div className="text-xs text-amber-800 dark:text-amber-200 flex items-center gap-2">
          <AlertCircle size={12} />
          {t('recurringPanel.loadError')}
          <button
            type="button"
            onClick={() => void load()}
            className="underline hover:no-underline"
          >
            {t('recurringPanel.retry')}
          </button>
        </div>
      ) : items === null ? (
        <p className="text-xs t-faint">{t('recurringPanel.loading')}</p>
      ) : items.length === 0 ? (
        <p className="text-xs t-faint">
          {isWon ? t('recurringPanel.emptyProject') : t('recurringPanel.emptyOpportunity')}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((r) => {
            const vigencia =
              r.maxOccurrences != null
                ? t('recurringPanel.quotas', { count: r.maxOccurrences })
                : r.endsAt != null
                  ? t('recurringPanel.until', { date: fmtDate(r.endsAt) })
                  : t('recurringPanel.indefinite')
            return (
              <li
                key={r.id}
                className="rounded-lg border border-base/60 px-3 py-2 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="t-primary text-sm font-medium truncate">{r.name}</p>
                    <span
                      className={`shrink-0 text-[10px] px-1.5 py-0.5 rounded border ${
                        STATUS_STYLES[r.status] ?? STATUS_STYLES.COMPLETED
                      }`}
                    >
                      {t(`enums:RecurringInvoiceStatus.${r.status}`)}
                    </span>
                  </div>
                  <p className="text-xs t-muted">
                    {t(`enums:RecurringInvoicePeriodicity.${r.periodicity}`)} · {vigencia}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono t-primary text-sm">{formatCents(perPeriodCents(r))}</p>
                  <p className="text-[11px] t-muted">
                    {t('recurringPanel.next', { date: fmtDate(r.nextRunDate) })}
                  </p>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {/* Pactado en la venta y todavía sin iguala: ni factura, ni lo hará solo.
          Se explica y NO se manda crear desde aquí: el plan de facturación es
          el único camino que respeta la vigencia y el ancla de inicio. */}
      {pactadasSinIguala.length > 0 && (
        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3">
          <p className="text-xs text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
            <AlertTriangle size={12} /> {t('recurringPanel.agreedTitle')}
          </p>
          <ul className="mt-1.5 space-y-1">
            {pactadasSinIguala.map((l) => (
              <li key={l.id} className="text-xs t-secondary flex justify-between gap-3">
                <span className="truncate">
                  {t(`enums:ServiceLine.${l.code}`, { defaultValue: l.code })}
                  {l.periodicity && (
                    <span className="t-faint">
                      {' · '}
                      {t(`enums:RecurringInvoicePeriodicity.${l.periodicity}`)}
                    </span>
                  )}
                  {l.occurrences != null && (
                    <span className="t-faint">
                      {' '}
                      · {t('recurringPanel.quotas', { count: l.occurrences })}
                    </span>
                  )}
                </span>
                <span className="font-mono t-muted shrink-0">{formatCents(l.valueCents)}</span>
              </li>
            ))}
          </ul>
          <p className="text-[11px] t-faint mt-1.5">{t('recurringPanel.agreedHint')}</p>
        </div>
      )}

      {creating && clientId && (
        <RecurringFormModal
          initial={null}
          lockedClientId={clientId}
          lockedOpportunityId={opportunityId}
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false)
            await load()
          }}
        />
      )}
    </div>
  )
}
